/**
 * Customer Type Mapping Utility
 *
 * Provides functions to map between legacy string-based customer types
 * and the new customer_types table UUID-based type_id system.
 *
 * This ensures backward compatibility during the transition from
 * the legacy 'type' field to the new 'type_id' foreign key.
 */

import { fetchCustomerTypes } from '../supabase-client'

// Cache for customer types to avoid repeated database queries
let customerTypesCache = null
let cacheTimestamp = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

/**
 * Get customer types with caching
 * @returns {Promise<Array>} Array of customer type objects
 */
export const getCachedCustomerTypes = async () => {
  const now = Date.now()

  // Return cached data if still valid
  if (customerTypesCache && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
    return customerTypesCache
  }

  // Fetch fresh data
  customerTypesCache = await fetchCustomerTypes(true) // activeOnly = true
  cacheTimestamp = now

  return customerTypesCache
}

/**
 * Invalidate the customer types cache
 * Call this after creating, updating, or deleting customer types
 */
export const invalidateCustomerTypesCache = () => {
  customerTypesCache = null
  cacheTimestamp = null
}

/**
 * Map a legacy type name (string) to a type_id (UUID)
 *
 * @param {string} typeName - Legacy type name (e.g., 'grocer', 'restaurant')
 * @returns {Promise<string|null>} UUID of matching customer type, or null if not found
 *
 * @example
 * const typeId = await mapTypeNameToTypeId('grocer')
 * // Returns UUID like '123e4567-e89b-12d3-a456-426614174000'
 */
export const mapTypeNameToTypeId = async (typeName) => {
  if (!typeName) return null

  const customerTypes = await getCachedCustomerTypes()
  const normalizedName = typeName.toLowerCase().trim()

  const matchingType = customerTypes.find(
    type => type.name.toLowerCase() === normalizedName
  )

  return matchingType ? matchingType.id : null
}

/**
 * Map a type_id (UUID) to a legacy type name (string)
 *
 * @param {string} typeId - UUID of customer type
 * @returns {Promise<string|null>} Legacy type name, or null if not found
 *
 * @example
 * const typeName = await mapTypeIdToTypeName('123e4567-e89b-12d3-a456-426614174000')
 * // Returns 'grocer'
 */
export const mapTypeIdToTypeName = async (typeId) => {
  if (!typeId) return null

  const customerTypes = await getCachedCustomerTypes()

  const matchingType = customerTypes.find(type => type.id === typeId)

  return matchingType ? matchingType.name.toLowerCase() : null
}

/**
 * Batch map multiple type names to type_ids
 * More efficient than calling mapTypeNameToTypeId multiple times
 *
 * @param {Array<string>} typeNames - Array of legacy type names
 * @returns {Promise<Object>} Map of typeName -> typeId
 *
 * @example
 * const mapping = await batchMapTypeNamesToTypeIds(['grocer', 'restaurant', 'cafe'])
 * // Returns { 'grocer': 'uuid1', 'restaurant': 'uuid2', 'cafe': 'uuid3' }
 */
export const batchMapTypeNamesToTypeIds = async (typeNames) => {
  const customerTypes = await getCachedCustomerTypes()
  const mapping = {}

  typeNames.forEach(typeName => {
    if (!typeName) return

    const normalizedName = typeName.toLowerCase().trim()
    const matchingType = customerTypes.find(
      type => type.name.toLowerCase() === normalizedName
    )

    if (matchingType) {
      mapping[typeName] = matchingType.id
    }
  })

  return mapping
}

/**
 * Get default customer type ID (typically 'grocer' as most common)
 * @returns {Promise<string|null>} UUID of default customer type
 */
export const getDefaultCustomerTypeId = async () => {
  return await mapTypeNameToTypeId('grocer')
}
